import { Canvas, invalidate } from '@react-three/fiber';
import classNames from 'classnames';
import { useCallback, useImperativeHandle, useRef } from 'react';
import type { ReactElement, RefObject } from 'react';
import type { ShapeType } from '../Superpowers.data';
import type { SuperpowerWebglProps, SuperpowerWebglRef } from './SuperpowerWebgl.data';
import styles from './SuperpowerWebgl.module.scss';
import Scene from './scene/Scene';
import type { SceneWebglRef } from './scene/Scene.data';
import { getMinMaxDpr } from 'util/webgl.utils';
import { AdaptiveDpr, AdaptiveEvents } from '@react-three/drei';
import { isTouch } from '@/hooks/useIsDesktopDevice';

export default function SuperpowerWebgl({
  ref,
  slides,
  className,
}: SuperpowerWebglProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<SuperpowerWebglRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const sceneWebglRef = useRef<SceneWebglRef>(null);
  const currentSlide = useRef(0);
  // const [dpr, setDpr] = useState(getMinMaxDpr()[1]);

  const setProgress = useCallback((value: number) => {
    // console.log(value, 'setProgress');
    if (sceneWebglRef?.current) {
      sceneWebglRef.current.setProgress(value);
      invalidate();
    }
  }, []);

  const setCurrentSlide = useCallback((slide: number) => {
    // console.log(value, 'setProgress');
    currentSlide.current = slide;
    if (sceneWebglRef?.current) sceneWebglRef.current.setCurrentSlide(slide);
  }, []);

  const setProgressData = useCallback(
    (
      progressData: Array<{
        id: ShapeType;
        progress: number;
      }>,
    ) => {
      if (sceneWebglRef?.current) sceneWebglRef.current.setProgressData(progressData);
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      setCurrentSlide,
      setProgressData,
      elementRef,
    }),
    [setProgressData, elementRef, setProgress, setCurrentSlide],
  );

  return (
    <div ref={elementRef} className={classNames(styles.superpowerWebgl, className)}>
      <div className={classNames(styles.isInViewWrap, className)}>
        <Canvas
          frameloop="always"
          gl={{
            logarithmicDepthBuffer: true,
            toneMappingExposure: 1.5,
          }}
          dpr={isTouch ? [1, 1.4] : getMinMaxDpr()}
        >
          <Scene ref={sceneWebglRef} slides={slides} />
          <AdaptiveDpr pixelated />
          <AdaptiveEvents />
        </Canvas>
      </div>
    </div>
  );
}
